<template>
  <div>
  <UiDropdown
    class="user-avatar-menu terminal-icon-control"
    trigger="click"
    placement="bottom-start"
    @command="handleCommand"
    @visible-change="avatarMenuVisible = $event"
  >
    <div class="avatar-wrapper" :aria-label="$t('用户菜单')" :title="$t('用户菜单')">
      <img v-if="hasImageAvatar" :src="`${avatarUrl}?imageView2/1/w/80/h/80`" class="user-avatar" />
      <UiAvatar v-else size="medium" icon="el-icon-user-solid"></UiAvatar>
      <i class="el-icon-caret-bottom" :class="{ 'is-open': avatarMenuVisible }"></i>
    </div>

    <el-dropdown-menu slot="dropdown" class="terminal-dropdown-menu terminal-user-dropdown">
      <el-dropdown-item command="go-adminEdit" class="terminal-user-dropdown-item">
        <i class="el-icon-user-solid"></i>
        <span class="terminal-user-name">{{ user.name }}</span>
      </el-dropdown-item>
      <el-dropdown-item v-if="showFullscreenAction" command="toggle-fullscreen" class="terminal-user-dropdown-item">
        <i :class="isFullscreen ? 'iconfont icon-chuangkou-zuixiaohua' : 'el-icon-full-screen'"></i>
        <span>{{ $t(isFullscreen ? '还原窗口' : '切换全屏') }}</span>
      </el-dropdown-item>
      <el-dropdown-item v-if="showPrinterAction" command="connect-printer" class="terminal-user-dropdown-item">
        <i class="iconfont icon-dayin-dayinji"></i>
        <span>{{ $t("连接打印机") }}</span>
      </el-dropdown-item>
      <el-dropdown-item v-if="canOpenGround" divided command="go-ground" class="terminal-user-dropdown-item">
        <i class="el-icon-monitor"></i><span>{{ $t("地面站") }}</span>
      </el-dropdown-item>
      <el-dropdown-item :divided="!canOpenGround" command="go-backend" class="terminal-user-dropdown-item">
        <i class="el-icon-menu"></i><span>{{ $t("后台管理") }}</span>
      </el-dropdown-item>
      <el-dropdown-item v-if="hasBusinessPermission" command="go-setting" class="terminal-user-dropdown-item">
        <i class="iconfont icon-shezhi"></i>
        <span>{{ $t("设置") }}</span>
      </el-dropdown-item>
      <el-dropdown-item v-if="hasBusinessPermission" command="go-help" class="terminal-user-dropdown-item">
        <i class="iconfont icon-bangzhu"></i>
        <span>{{ $t("帮助") }}</span>
      </el-dropdown-item>
      <el-dropdown-item divided command="logout" class="terminal-user-dropdown-item">
        <i class="iconfont icon-logout"></i>
        <span>{{ $t("退出登录") }}</span>
      </el-dropdown-item>
    </el-dropdown-menu>
  </UiDropdown>
  </div>
</template>

<script>
import { t } from '@/i18n'
import { connectReceiptPrinter, toggleDocumentFullscreen } from '@/utils/clientTools'

export default {
  name: 'UserAvatarMenu',
  props: { canOpenGround: { type: Boolean, default: false } },
  data () {
    return {
      avatarMenuVisible: false,
      isFullscreen: false
    }
  },
  computed: {
    user () { return this.$store.state.user || {} },
    avatarUrl () { return `${this.$store.state.settings.photoPath || ''}${this.user.photo || ''}` },
    hasImageAvatar () { return /\.(jpg|jpeg|gif|webp|png)(?:$|\?)/i.test(this.avatarUrl) },
    isWebClient () { return this.$store.getters['app/getIsWebClient'] },
    isCordovaAndroid () { return this.$store.getters['app/getIsCordovaAndroid'] },
    showFullscreenAction () {
      return this.isWebClient && this.$store.getters['app/getStatusBarButtonVisible']('fullscreen')
    },
    showPrinterAction () {
      return this.isCordovaAndroid && this.$store.getters['app/getStatusBarButtonVisible']('printer')
    },
    hasBusinessPermission () {
      return this.user.is_god === true || (Array.isArray(this.user.permissions) && this.user.permissions.length > 0)
    }
  },
  mounted () {
    this.syncFullscreenState()
    document.addEventListener('fullscreenchange', this.syncFullscreenState)
    document.addEventListener('webkitfullscreenchange', this.syncFullscreenState)
  },
  beforeDestroy () {
    document.removeEventListener('fullscreenchange', this.syncFullscreenState)
    document.removeEventListener('webkitfullscreenchange', this.syncFullscreenState)
  },
  methods: {
    syncFullscreenState () {
      this.isFullscreen = Boolean(document.fullscreenElement || document.webkitFullscreenElement)
    },
    handleCommand (command) {
      if (command === 'go-ground') {
        this.$emit('open-ground')
        return
      }
      if (command === 'go-backend') {
        this.$emit('open-admin')
        return
      }
      if (command === 'go-setting') this.$router.push('/settings/index')
      else if (command === 'go-help') this.$router.push('/help/index')
      else if (command === 'go-adminEdit') this.$router.push(`/admin/edit/${this.user.id}`)
      else if (command === 'toggle-fullscreen') this.toggleFullscreen()
      else if (command === 'connect-printer') this.connectPrinter()
      else if (command === 'logout') this.logout()
    },
    async toggleFullscreen () {
      try {
        this.isFullscreen = await toggleDocumentFullscreen()
      } catch (error) {
        this.$message.warning(t(error.message || '全屏切换失败'))
      }
    },
    async connectPrinter () {
      try {
        await connectReceiptPrinter()
        this.$message.success(t('打印机已连接'))
      } catch (error) {
        this.$message.error(t(error.message || '打印机连接失败'))
      }
    },
    logout () {
      this.$store.dispatch('user/destroyUserAsync').then(() => this.$router.push('/login'))
    }
  }
}
</script>

<style lang="scss" scoped>
.user-avatar-menu {
  display: block;
  width: auto;
  height: var(--ui-icon-control-size);
  line-height: var(--ui-icon-control-size);
}

.avatar-wrapper {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  position: relative;
  cursor: pointer;
}

.user-avatar {
  width: 30px;
  height: 30px;
  display: block;
  border: 1px solid rgba(var(--ui-color-primary-rgb), .26);
  border-radius: 3px;
  box-sizing: border-box;
  object-fit: cover;
}

.el-icon-caret-bottom {
  color: var(--gs-dropdown-caret-color, var(--ui-text-muted));
  font-size: var(--gs-dropdown-caret-size, 12px);
  transition: transform .2s ease;
}

.el-icon-caret-bottom.is-open {
  transform: rotate(180deg);
}

.user-avatar-menu ::v-deep .el-avatar {
  width: 30px;
  height: 30px;
  border-radius: 3px;
  line-height: 30px;
  color: var(--ui-text-secondary);
  background: var(--ui-bg-elevated);
}
</style>

<style lang="scss">
.terminal-user-dropdown {
  min-width: 150px;
}

.terminal-user-dropdown .terminal-user-dropdown-item {
  min-width: 150px;
  height: 40px;
  padding: 0 var(--ui-space-lg);
  font-size: 14px;
  line-height: 40px;
}

.terminal-user-dropdown .terminal-user-dropdown-item > i {
  width: 18px;
  margin-right: var(--ui-space-sm);
  color: var(--ui-color-primary) !important;
  font-size: 16px;
  text-align: center;
  vertical-align: -1px;
}

.terminal-user-dropdown .terminal-user-dropdown-item > span {
  color: inherit;
  font-size: inherit;
  vertical-align: middle;
}

.terminal-user-dropdown .terminal-user-name {
  color: inherit !important;
}

.terminal-user-dropdown .terminal-user-dropdown-item.el-dropdown-menu__item--divided {
  margin-top: 0;
}
</style>
